import { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Send, Loader2, MessageSquare, Search, ChevronRight, ChevronLeft,
  PhoneOff, Users, GraduationCap,
} from "lucide-react";

const todayStr = () => new Date().toISOString().slice(0, 10);

// --- Grade classification helpers (Saudi school stages) ---
const STAGES = [
  { key: "primary", label: "ابتدائي", re: /ابتدائ|إبتدائ/ },
  { key: "middle", label: "متوسط", re: /متوسط/ },
  { key: "secondary", label: "ثانوي", re: /ثانوي/ },
  { key: "other", label: "أخرى", re: null },
];
const stageOf = (g = "") => (STAGES.find((s) => s.re && s.re.test(g)) || STAGES[3]).key;
const gradeOrder = (g = "") => {
  if (/سادس/.test(g)) return 6;
  if (/خامس/.test(g)) return 5;
  if (/راب[عة]/.test(g)) return 4;
  if (/ثالث/.test(g)) return 3;
  if (/ثاني/.test(g)) return 2;
  if (/أول|اول/.test(g)) return 1;
  const m = g.match(/\d+/);
  return m ? parseInt(m[0], 10) : 99;
};

export default function SendMessage() {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState("");
  const [activeStage, setActiveStage] = useState(null);
  const [openGrade, setOpenGrade] = useState(null);
  const [selected, setSelected] = useState({});
  const [templateId, setTemplateId] = useState("absence");
  const [custom, setCustom] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(0);

  useEffect(() => {
    api.get("/students").then((r) => setStudents(r.data)).catch(() => {});
    api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  // Build stage -> grade -> class tree
  const tree = useMemo(() => {
    const stages = {};
    students.forEach((s) => {
      const sk = stageOf(s.grade || "");
      const g = s.grade || "غير محدد";
      const c = s.class_name || "غير محدد";
      stages[sk] = stages[sk] || { count: 0, grades: {} };
      stages[sk].count += 1;
      stages[sk].grades[g] = stages[sk].grades[g] || { count: 0, classes: {} };
      stages[sk].grades[g].count += 1;
      stages[sk].grades[g].classes[c] = stages[sk].grades[g].classes[c] || [];
      stages[sk].grades[g].classes[c].push(s);
    });
    return stages;
  }, [students]);

  const presentStages = STAGES.filter((s) => tree[s.key]);
  const effectiveStage = activeStage && tree[activeStage] ? activeStage : presentStages[0]?.key;

  const gradesOfStage = useMemo(() => {
    const st = tree[effectiveStage];
    if (!st) return [];
    return Object.entries(st.grades)
      .sort((a, b) => gradeOrder(a[0]) - gradeOrder(b[0]) || a[0].localeCompare(b[0], "ar"))
      .map(([name, info]) => ({ name, ...info }));
  }, [tree, effectiveStage]);

  const searchResults = useMemo(() => {
    const q = search.trim();
    if (!q) return [];
    return students.filter(
      (s) => (s.name || "").includes(q) || (s.national_id || "").includes(q)
    );
  }, [search, students]);

  const selectedStudents = useMemo(
    () => students.filter((s) => selected[s.id]),
    [students, selected]
  );
  const hasPhone = (s) => !!(s.parent_phone && String(s.parent_phone).replace(/\D/g, ""));
  const willSend = selectedStudents.filter(hasPhone);
  const willSkip = selectedStudents.length - willSend.length;

  const setMany = (list, val) => {
    setSelected((prev) => {
      const next = { ...prev };
      list.forEach((s) => { if (val) next[s.id] = true; else delete next[s.id]; });
      return next;
    });
  };
  const allSelectedIn = (list) => list.length > 0 && list.every((s) => selected[s.id]);

  const template = templates.find((t) => t.id === templateId);
  const bodyTemplate = templateId === "custom" ? custom : template?.body || "";

  const previewPool = selectedStudents.length ? selectedStudents : students;
  const previewStudent = previewPool[previewIdx] || previewPool[0];
  const preview = bodyTemplate
    .replace(/{student_name}/g, previewStudent?.name || "اسم الطالب")
    .replace(/{parent_name}/g, "ولي الأمر")
    .replace(/{grade}/g, previewStudent?.grade || "")
    .replace(/{class}/g, previewStudent?.class_name || "")
    .replace(/{school_name}/g, user?.school_name || "المدرسة")
    .replace(/{date}/g, todayStr());

  const openConfirm = () => {
    if (willSend.length === 0) {
      toast.error("لا يوجد مستلمون لديهم رقم جوال. يرجى تحديد طلاب لديهم أرقام.");
      return;
    }
    if (templateId === "custom" && !custom.trim()) {
      toast.error("يرجى كتابة نص الرسالة المخصصة");
      return;
    }
    setConfirmOpen(true);
  };

  const doSend = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/messages/send", {
        student_ids: selectedStudents.map((s) => s.id),
        template_id: templateId,
        custom_body: templateId === "custom" ? custom : null,
      });
      setConfirmOpen(false);
      if (data.queued > 0) toast.success(`تم إرسال ${data.queued} رسالة إلى أولياء الأمور`);
      if (data.skipped > 0) toast.error(`تم تجاوز ${data.skipped} رسالة لعدم وجود رقم جوال`);
      if (!data.queued && !data.skipped) toast.error("لم يتم إرسال أي رسالة");
      setSelected({});
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSending(false);
    }
  };

  const StudentRow = ({ s }) => (
    <label
      data-testid={`select-student-${s.id}`}
      className="flex cursor-pointer items-center gap-3 rounded-lg border border-transparent px-3 py-2 hover:bg-emerald-50"
    >
      <Checkbox checked={!!selected[s.id]} onCheckedChange={(v) => setSelected({ ...selected, [s.id]: v })} />
      <div className="flex-1">
        <p className="text-sm font-semibold text-slate-800">{s.name}</p>
        <p className="text-xs text-slate-400">{s.grade} — {s.class_name}</p>
      </div>
      {hasPhone(s) ? (
        <span dir="ltr" className="font-mono text-xs text-slate-400">{s.parent_phone}</span>
      ) : (
        <span className="flex items-center gap-1 text-xs text-red-500"><PhoneOff className="h-3 w-3" /> بدون جوال</span>
      )}
    </label>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-800">إرسال رسالة</h1>
        <p className="text-slate-400">اختر المرحلة والصف والفصل، أو ابحث بالاسم، ثم أرسل الرسائل لأولياء الأمور</p>
      </div>

      {/* Live counters */}
      <div className="flex flex-wrap items-center gap-2" data-testid="send-counters">
        <Badge className="gap-1 bg-emerald-100 px-3 py-1.5 text-sm text-emerald-800">
          <Users className="h-4 w-4" /> المحدد: {selectedStudents.length}
        </Badge>
        <Badge className="gap-1 bg-emerald-700 px-3 py-1.5 text-sm text-white" data-testid="counter-will-send">
          <Send className="h-3.5 w-3.5" /> سيُرسل إلى {willSend.length}
        </Badge>
        {willSkip > 0 && (
          <Badge className="gap-1 bg-red-100 px-3 py-1.5 text-sm text-red-700" data-testid="counter-will-skip">
            <PhoneOff className="h-3.5 w-3.5" /> سيُتجاهل {willSkip} (بدون جوال)
          </Badge>
        )}
        {selectedStudents.length > 0 && (
          <button onClick={() => setSelected({})} className="text-xs text-slate-500 underline" data-testid="clear-selection">
            مسح التحديد
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Selection */}
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">1. اختيار الطلاب</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="بحث بالاسم أو رقم الهوية..."
                  className="pr-9"
                  data-testid="send-search"
                />
              </div>

              {search.trim() ? (
                /* Search mode */
                <div className="space-y-2">
                  <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Checkbox checked={allSelectedIn(searchResults)} onCheckedChange={(v) => setMany(searchResults, v)} data-testid="select-all-search" />
                      <span className="text-sm font-medium">تحديد نتائج البحث ({searchResults.length})</span>
                    </div>
                  </div>
                  <div className="max-h-96 space-y-1 overflow-y-auto pl-1">
                    {searchResults.map((s) => <StudentRow key={s.id} s={s} />)}
                    {searchResults.length === 0 && <p className="py-8 text-center text-sm text-slate-400">لا توجد نتائج</p>}
                  </div>
                </div>
              ) : students.length === 0 ? (
                <p className="py-10 text-center text-sm text-slate-400">لا يوجد طلاب. يرجى استيراد الطلاب أولاً.</p>
              ) : (
                /* Browse mode: stage -> grade -> class */
                <div className="space-y-4">
                  {/* Stage tabs */}
                  <div className="flex flex-wrap gap-2">
                    {presentStages.map((st) => (
                      <button
                        key={st.key}
                        onClick={() => { setActiveStage(st.key); setOpenGrade(null); }}
                        data-testid={`stage-tab-${st.key}`}
                        className={`rounded-full px-4 py-1.5 text-sm font-bold transition-all ${
                          effectiveStage === st.key
                            ? "bg-emerald-800 text-white shadow"
                            : "bg-slate-100 text-slate-600 hover:bg-emerald-100"
                        }`}
                      >
                        {st.label} ({tree[st.key].count})
                      </button>
                    ))}
                  </div>

                  {/* Grade boxes */}
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {gradesOfStage.map((g) => {
                      const gradeStudents = Object.values(g.classes).flat();
                      const selCount = gradeStudents.filter((s) => selected[s.id]).length;
                      const isOpen = openGrade === g.name;
                      return (
                        <button
                          key={g.name}
                          onClick={() => setOpenGrade(isOpen ? null : g.name)}
                          data-testid={`grade-box-${g.name}`}
                          className={`flex items-center gap-2 rounded-xl border-2 p-3 text-right transition-all ${
                            isOpen ? "border-emerald-700 bg-emerald-50" : "border-slate-200 hover:border-emerald-300"
                          }`}
                        >
                          <GraduationCap className={`h-5 w-5 shrink-0 ${isOpen ? "text-emerald-700" : "text-slate-400"}`} />
                          <div className="flex-1">
                            <span className="block text-sm font-bold text-slate-800">{g.name}</span>
                            <span className="text-xs text-slate-400">{g.count} طالب</span>
                          </div>
                          {selCount > 0 && <Badge className="bg-emerald-700 text-white">{selCount}</Badge>}
                        </button>
                      );
                    })}
                  </div>

                  {/* Classes of opened grade */}
                  {openGrade && tree[effectiveStage]?.grades[openGrade] && (
                    <div className="space-y-3 rounded-xl bg-slate-50 p-3" data-testid="grade-classes">
                      {Object.entries(tree[effectiveStage].grades[openGrade].classes)
                        .sort((a, b) => a[0].localeCompare(b[0], "ar"))
                        .map(([cls, list]) => (
                          <div key={cls} className="rounded-lg bg-white p-2 shadow-sm">
                            <div className="flex items-center justify-between border-b px-2 py-1">
                              <label className="flex cursor-pointer items-center gap-2">
                                <Checkbox checked={allSelectedIn(list)} onCheckedChange={(v) => setMany(list, v)} data-testid={`select-class-${cls}`} />
                                <span className="text-sm font-bold text-slate-700">الفصل {cls}</span>
                              </label>
                              <Badge variant="outline" className="text-xs">{list.length} طالب</Badge>
                            </div>
                            <div className="max-h-56 space-y-1 overflow-y-auto pt-1">
                              {list.map((s) => <StudentRow key={s.id} s={s} />)}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Template */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">2. اختيار القالب</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {templates.map((t) => (
                  <button key={t.id} onClick={() => setTemplateId(t.id)} data-testid={`template-${t.id}`}
                    className={`rounded-xl border-2 p-3 text-right transition-all ${
                      templateId === t.id ? "border-emerald-700 bg-emerald-50" : "border-slate-200 hover:border-emerald-300"
                    }`}>
                    <span className="block text-sm font-bold text-slate-800">{t.title}</span>
                    <span className="text-xs text-slate-400">{t.badge}</span>
                  </button>
                ))}
              </div>
              {templateId === "custom" && (
                <div>
                  <Label className="mb-1 block text-xs text-slate-500">نص الرسالة المخصصة</Label>
                  <Textarea value={custom} onChange={(e) => setCustom(e.target.value)} rows={4}
                    data-testid="custom-message" placeholder="يمكنك استخدام: {student_name} {school_name} {grade} {class} {date}" />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Preview */}
        <div className="space-y-4">
          <Card className="sticky top-20">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <MessageSquare className="h-4 w-4 text-emerald-700" /> معاينة الرسالة
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {previewPool.length > 1 && (
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <Button variant="ghost" size="icon" className="h-7 w-7"
                    onClick={() => setPreviewIdx((i) => (i - 1 + previewPool.length) % previewPool.length)}
                    data-testid="preview-prev">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <span>عينة {Math.min(previewIdx, previewPool.length - 1) + 1} من {previewPool.length} · {previewStudent?.name}</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7"
                    onClick={() => setPreviewIdx((i) => (i + 1) % previewPool.length)}
                    data-testid="preview-next">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                </div>
              )}
              <div className="rounded-2xl bg-[#e5ddd5] p-3">
                <div className="ml-auto max-w-full rounded-lg rounded-tr-none bg-[#dcf8c6] p-3 text-sm leading-relaxed text-slate-800 shadow-sm"
                  data-testid="message-preview">
                  {preview || "اختر قالباً لعرض المعاينة"}
                </div>
              </div>
              <Button onClick={openConfirm} disabled={willSend.length === 0}
                className="h-12 w-full gap-2 bg-emerald-800 text-base font-bold hover:bg-emerald-900"
                data-testid="send-whatsapp-button">
                <Send className="h-5 w-5" /> إرسال عبر واتساب ({willSend.length})
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="text-right">
          <AlertDialogHeader>
            <AlertDialogTitle>تأكيد الإرسال</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>سيتم إرسال رسالة <span className="font-bold text-emerald-800">«{template?.title}»</span> إلى:</p>
                <div className="rounded-lg bg-emerald-50 p-3">
                  <p>✅ <span className="font-bold text-emerald-800">{willSend.length}</span> ولي أمر لديهم رقم جوال</p>
                  {willSkip > 0 && <p className="text-red-600">⚠️ سيتم تجاوز {willSkip} طالب بدون رقم جوال</p>}
                </div>
                {willSend.length > 5 && (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-800" data-testid="ban-warning">
                    <span className="text-base">🤍</span>
                    <p className="text-xs leading-relaxed">
                      تنبيه بسيط: إرسال عدد كبير من الرسائل دفعة واحدة قد يعرّض رقمك لخطر الحظر من واتساب.
                      ننصح بتقسيم الإرسال على دفعات أصغر. يمكنك المتابعة إن رغبت.
                    </p>
                  </div>
                )}
                <p>هل تريد المتابعة؟</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-send">إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doSend(); }} disabled={sending}
              className="gap-2 bg-emerald-800 hover:bg-emerald-900" data-testid="confirm-send">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "تأكيد الإرسال"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
